import { useCallback } from "react";
import { useAuth } from "src/context";

export type QueryParamsType = Record<
  string,
  string | number | boolean | Array<string | number | boolean>
>;
type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  queryParams?: QueryParamsType;
  responseType?: "json" | "blob";
}

export type FetchFromApiType = (
  path: string,
  options?: RequestOptions,
) => Promise<unknown>;

export const useApiClient = () => {
  const { user } = useAuth();

  const fetchFromApi: FetchFromApiType = useCallback(
    async (path, options) => {
      const { method = "GET", body, queryParams } = options || {};
      const url = new URL(path, __API_BASE_URL__);
      if (!user) {
        throw new Error("Authentication is required to call the API.");
      }
      const token = await user.getIdToken();

      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              url.searchParams.append(key, String(item));
            });
          } else {
            url.searchParams.append(key, String(value));
          }
        });
      }

      const res = await fetch(url.toString(), {
        method,
        headers: {
          "content-type": "application/json",
          "x-jwt": token,
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });

      if (!res.ok) {
        let errorMessage =
          `${method} API request failed with status: ` + res.status;
        let errorBody: unknown;

        try {
          const parsedBody = (await res.json()) as unknown;
          errorBody = parsedBody;
          if (
            typeof parsedBody === "object" &&
            parsedBody !== null &&
            "message" in parsedBody &&
            typeof parsedBody.message === "string"
          ) {
            errorMessage = parsedBody.message;
          }
        } catch {
          errorBody = "Failed to parse error response";
        }

        const error = Object.assign(new Error(errorMessage), {
          status: res.status,
          body: errorBody,
        });
        throw error;
      }

      if (options?.responseType === "blob") {
        const blob = await res.blob();
        const headers: Record<string, string> = {};
        res.headers.forEach((value, key) => {
          headers[key.toLowerCase()] = value;
        });
        return { blob, headers };
      } else {
        const text = await res.text();
        return text ? (JSON.parse(text) as unknown) : null;
      }
    },
    [user],
  );

  return { fetchFromApi };
};
